import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Download, Menu, X } from "lucide-react";
import { scrollToId } from "@/lib/scroll";
import { openDownloads } from "@/lib/api";

const LINKS = [
  { id: "vault", label: "The Vault" },
  { id: "licensing", label: "Licensing" },
  { id: "producer", label: "The Producer" },
  { id: "contact", label: "Contact" },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <>
      <header
        className={`fixed top-0 w-full z-50 transition-colors duration-300 ${
          scrolled ? "bg-black/60 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
        }`}
      >
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          <button
            data-testid="nav-logo-btn"
            onClick={() => go("top")}
            className="flex items-center gap-3 group"
            aria-label="DJ Miami home"
          >
            <img
              src="/images/logo-vice.jpg"
              alt="DJ Miami logo"
              className="w-10 h-10 object-cover group-hover:shadow-[0_0_24px_rgba(214,91,180,0.4)] transition-shadow"
            />
            <span className="font-display font-bold text-sm tracking-widest uppercase">DJ Miami</span>
          </button>

          <nav className="hidden md:flex items-center gap-8">
            {LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`nav-link-${l.id}`}
                onClick={() => go(l.id)}
                className="text-xs uppercase tracking-[0.2em] text-white/60 hover:text-white transition-colors relative group"
              >
                {l.label}
                <span className="absolute -bottom-1 left-0 w-0 h-px bg-[#D65BB4] group-hover:w-full transition-all duration-300" />
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button
              data-testid="nav-downloads-btn"
              onClick={openDownloads}
              className="hidden sm:flex items-center gap-2 border border-white/20 px-4 py-2 text-xs uppercase tracking-widest hover:border-[#4DBDBE] hover:text-[#4DBDBE] transition-colors"
            >
              <Download size={14} /> My Downloads
            </button>
            <button
              data-testid="nav-cta-btn"
              onClick={() => go("vault")}
              className="hidden sm:block bg-[#D65BB4] px-5 py-2 text-xs uppercase tracking-widest font-bold hover:shadow-[0_0_32px_rgba(214,91,180,0.45)] transition-shadow"
            >
              Tap In
            </button>
            <button
              data-testid="nav-mobile-toggle"
              className="md:hidden p-2"
              onClick={() => setOpen(!open)}
              aria-label="Toggle menu"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 bg-black/95 backdrop-blur-xl md:hidden flex flex-col items-center justify-center gap-8"
          >
            {LINKS.map((l, i) => (
              <motion.button
                key={l.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 * i }}
                data-testid={`nav-mobile-link-${l.id}`}
                onClick={() => go(l.id)}
                className="font-display text-2xl uppercase"
              >
                {l.label}
              </motion.button>
            ))}
            <button
              data-testid="nav-mobile-downloads-btn"
              onClick={() => { setOpen(false); openDownloads(); }}
              className="flex items-center gap-2 border border-white/20 px-6 py-3 text-sm uppercase tracking-widest"
            >
              <Download size={16} /> My Downloads
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
